<template>
  <PVMenu
    ref="menuRef"
    v-bind="{ ...$props, ...$attrs }"
    :model="menuItems"
    :pt="passThroughProps"
    :base-z-index="baseZIndex ?? POPOVER_Z_INDEX"
    @show="onShow"
  >
    <template
      v-if="$slots.start"
      #start
    >
      <slot name="start" />
    </template>
    <template #itemicon="slotProps">
      <slot
        name="itemicon"
        v-bind="slotProps"
      >
        <icon
          v-if="slotProps.item && typeof slotProps.item.icon === 'string'"
          :icon="slotProps.item.icon"
          fixed-width
          :class="[slotProps.item?.iconClass]"
          class="mr-1"
        />
        <span
          v-else
          class="fa-fw"
          aria-hidden="true"
        />
      </slot>
    </template>
  </PVMenu>
</template>
<script lang="ts">
import type { MenuItem } from 'primevue/menuitem';
import type { StandardDropdownProps } from '@/types/StandardDropdownProps';

export type StandardMenuProps = StandardDropdownProps & {
  menuItems: MenuItem[];
  popup?: boolean;
  listClass?: string;
};

export default {
  name: 'StandardMenu',
  compatConfig: { MODE: 3 },
};
</script>
<script setup lang="ts">
import { ref, computed } from 'vue';
import type PVMenu from 'primevue/menu';
import type { MenuPassThroughOptions } from 'primevue/menu';
import { PassThrough } from '@primevue/core';
import { getAlignDropdownRightUtils, DropdownInstance } from '@/utils/getAlignDropdownRightUtils';
import { POPOVER_Z_INDEX } from '@/constants/ComponentZIndices';

type MenuInstance = {
  toggle: (event: Event, target?: HTMLElement) => void;
  hide: () => void;
} & DropdownInstance;

const props = withDefaults(defineProps<StandardMenuProps>(), {
  dropdownAlign: 'start',
  shouldMatchTriggerWidth: false,
});

defineEmits<{
  (e: 'show'): void;
  (e: 'hide'): void;
}>();

const menuRef = ref<MenuInstance | null>(null);

const { alignMenuWithEnd, ptOverride } = getAlignDropdownRightUtils({ dropdownRef: menuRef });

const passThroughProps = computed(
  (): PassThrough<MenuPassThroughOptions> => ({
    /**
     * Popup Menu paints Bootstrap `.dropdown-menu` on the positioned root so
     * overlay.offsetWidth is the visible panel (same as FreeformDropdown). Keep the
     * list in flow so the root sizes to it instead of collapsing around overlay chrome.
     */
    root: props.popup
      ? {
          class: 'dropdown-menu shadow show',
        }
      : {
          // The searchable menu already sits in a dropdown panel. Drop this inner border.
          class: 'border-0',
        },
    list: {
      class: [props.popup ? 'p-0 mb-0' : props.listClass || 'dropdown-menu shadow show'],
      ...(props.popup ? { style: { position: 'static' } } : {}),
    },
    itemLink: {
      class: 'no-hover text-decoration-none',
    },
    ...ptOverride,
  }),
);

const toggle = (e: MouseEvent) => {
  menuRef.value?.toggle(e);
};

const onShow = () => {
  if (props.dropdownAlign === 'end') {
    alignMenuWithEnd({ shouldDropdownMatchTriggerWidth: props.shouldMatchTriggerWidth });
  }
};

// expose for the parent to call
defineExpose({
  menuRef,
  toggle,
});
</script>
